import { useEffect } from "react";
import { toast } from "sonner";

export const IDLE_LIMIT_MS = 30 * 60 * 1000;   // 30 min without activity → logout
const HEARTBEAT_MS = 15 * 1000;                 // open tabs write a heartbeat
const CLOSED_GRACE_MS = 60 * 1000;              // no heartbeat for >60 s = browser was closed
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click", "wheel"];

const LS_ACTIVITY = "app_last_activity";
const LS_HEARTBEAT = "app_heartbeat";

// Call once at startup, BEFORE restoring the session. Returns a reason string
// if the stored session must be discarded ("idle" | "closed"), else null.
export const staleSessionReason = () => {
  if (!localStorage.getItem("token")) return null;
  const now = Date.now();
  const last = Number(localStorage.getItem(LS_ACTIVITY) || 0);
  const hb = Number(localStorage.getItem(LS_HEARTBEAT) || 0);
  if (last && now - last > IDLE_LIMIT_MS) return "idle";
  if (hb && now - hb > CLOSED_GRACE_MS) return "closed";
  return null;
};

export const clearSessionMarkers = () => {
  localStorage.removeItem(LS_ACTIVITY);
  localStorage.removeItem(LS_HEARTBEAT);
};

export const logoutMessage = (reason) =>
  reason === "idle"
    ? "You were signed out after 30 minutes of inactivity."
    : "You were signed out because the browser was closed.";

// Active while a user is logged in: tracks activity (shared across tabs via
// localStorage), writes a heartbeat, and logs out on idle / cross-tab logout.
export const useAutoLogout = (user, onLogout) => {
  useEffect(() => {
    if (!user) return undefined;
    const now = Date.now();
    localStorage.setItem(LS_ACTIVITY, String(now));
    localStorage.setItem(LS_HEARTBEAT, String(now));

    let lastWrite = now;
    const touch = () => {
      const t = Date.now();
      if (t - lastWrite > 5000) {
        lastWrite = t;
        localStorage.setItem(LS_ACTIVITY, String(t));
      }
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, touch, { passive: true }));

    const doLogout = (reason) => {
      onLogout();
      clearSessionMarkers();
      toast.warning(logoutMessage(reason), { duration: 8000 });
    };

    const tick = () => {
      const t = Date.now();
      localStorage.setItem(LS_HEARTBEAT, String(t));
      const last = Number(localStorage.getItem(LS_ACTIVITY) || t);
      if (t - last > IDLE_LIMIT_MS) doLogout("idle");
    };
    const timer = setInterval(tick, HEARTBEAT_MS);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);

    // Logged out in another tab → mirror here
    const onStorage = (e) => {
      if (e.key === "token" && e.newValue === null) onLogout();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, touch));
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
    };
  }, [user, onLogout]);
};
